import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import {
  Category,
  GeneratedContent,
  ProjectEpic,
  ProjectErrorMessage,
  ProjectFeature,
  ProjectSectionAsset,
  ProjectStory,
  ProjectWireframe,
} from '../../types/content';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import ProjectAiPanel from '../../components/content/ProjectAiPanel';
import ProjectRequirementsTree from '../../components/content/ProjectRequirementsTree';
import ProjectSectionAssets from '../../components/content/ProjectSectionAssets';
import BlockEditor, { blocksFromPlainText } from '../../components/content/BlockEditor';
import { canWriteProjects, submitMessage } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import { UnsavedLeaveDialog, useDirtyBaseline, useUnsavedChangesGuard } from '../../components/content/UnsavedLeaveDialog';
import { useAutoClear } from '../../hooks/useAutoClear';
import ContentOwnerSelect from '../../components/content/ContentOwnerSelect';
import { getUserProfileId } from '../../utils/userProfile';

type Section = 'overview' | 'spec' | 'requirements' | 'wireframes' | 'architecture' | 'errors';

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'spec', label: 'Spec' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'wireframes', label: 'Wireframes' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'errors', label: 'Error messages' },
];

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`);

const generatedMarkdown = (content: GeneratedContent) => {
  const sections = (content.sections || []).map((section) => {
    const body = (section.blocks || []).map((block) => block.content).filter(Boolean).join('\n');
    return `## ${section.heading}\n${body}`.trim();
  });
  return [
    content.title ? `# ${content.title}` : '',
    content.objective || '',
    ...sections,
    (content.keyTakeaways || []).length ? `## Key takeaways\n${content.keyTakeaways.map((item) => `- ${item}`).join('\n')}` : '',
  ].filter(Boolean).join('\n\n');
};

const ProjectEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const gigId = id ? Number(id) : undefined;
  const [section, setSection] = useState<Section>('overview');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [summary, setSummary] = useState('');
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [engagementType, setEngagementType] = useState('Contract');
  const [compensation, setCompensation] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [teamType, setTeamType] = useState('Individual');
  const [techStack, setTechStack] = useState('');
  const [applyUrl, setApplyUrl] = useState('');
  const [spec, setSpec] = useState('');
  const [specBlocks, setSpecBlocks] = useState(blocksFromPlainText(''));
  const [architectureOverview, setArchitectureOverview] = useState('');
  const [architectureBlocks, setArchitectureBlocks] = useState(blocksFromPlainText(''));
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [difficulty, setDifficulty] = useState('Beginner');
  const [tags, setTags] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [authorId, setAuthorId] = useState<number | ''>('');
  const [authorLabel, setAuthorLabel] = useState('');
  const [epics, setEpics] = useState<ProjectEpic[]>([]);
  const [features, setFeatures] = useState<ProjectFeature[]>([]);
  const [stories, setStories] = useState<ProjectStory[]>([]);
  const [wireframes, setWireframes] = useState<ProjectWireframe[]>([]);
  const [errorMessages, setErrorMessages] = useState<ProjectErrorMessage[]>([]);
  const [sectionAssets, setSectionAssets] = useState<ProjectSectionAsset[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(!!gigId);

  useEffect(() => {
    contentApi.listCategories('Project').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!gigId) return;
    setLoading(true);
    contentApi.getProject(gigId).then((r) => {
      const gig = r.data.project;
      setTitle(gig.title);
      setSubtitle(gig.subtitle || '');
      setSummary(gig.summary || '');
      setCompany(gig.company);
      setLocation(gig.location || '');
      setEngagementType(gig.engagementType || 'Contract');
      setCompensation(gig.compensation || '');
      setEstimatedHours(gig.estimatedHours || '');
      setTeamType(gig.teamType === 'Group' ? 'Group' : 'Individual');
      setTechStack(gig.techStack || '');
      setApplyUrl(gig.applyUrl || '');
      setSpec(gig.spec || '');
      setSpecBlocks(gig.specBlocks?.length ? gig.specBlocks : blocksFromPlainText(gig.spec));
      setArchitectureOverview(gig.architectureOverview || '');
      setArchitectureBlocks(gig.architectureBlocks?.length ? gig.architectureBlocks : blocksFromPlainText(gig.architectureOverview));
      setCategoryId(gig.categoryId || '');
      setDifficulty(gig.difficulty || 'Beginner');
      setTags((gig.tags || []).join(', '));
      setIsPublic(gig.isPublic !== false);
      setAuthorId(gig.author?.authorId || '');
      setAuthorLabel(gig.author?.displayName || '');
      const loadedEpics = (gig.epics && gig.epics.length > 0)
        ? gig.epics.map((epic, i) => ({ ...epic, clientKey: epic.clientKey || `epic-${epic.id || i}`, displayOrder: i + 1 }))
        : (gig.epicTitle
          ? [{ title: gig.epicTitle, description: gig.epicDescription || '', displayOrder: 1, clientKey: newKey() }]
          : []);
      setEpics(loadedEpics);
      const nextFeatures = (gig.features || []).map((feature, i) => ({
        ...feature,
        clientKey: feature.clientKey || (feature.id ? `f-${feature.id}` : newKey()),
        epicClientKey: feature.epicClientKey || loadedEpics.find((e) => e.id === feature.epicId)?.clientKey || '',
        displayOrder: i + 1,
      }));
      setFeatures(nextFeatures);
      setStories((gig.stories || []).map((story, i) => ({
        ...story,
        clientKey: story.clientKey || (story.id ? `s-${story.id}` : newKey()),
        featureClientKey: story.featureClientKey || nextFeatures.find((f) => f.id === story.featureId)?.clientKey || '',
        displayOrder: i + 1,
      })));
      setWireframes(gig.wireframes || []);
      setErrorMessages(gig.errorMessages || []);
      setSectionAssets(gig.sectionAssets || []);
    }).catch(() => setError('Unable to load project.')).finally(() => setLoading(false));
  }, [gigId]);

  useEffect(() => {
    if (gigId) return;
    const profileId = getUserProfileId(user);
    if (!profileId) return;
    setAuthorId(profileId);
    setAuthorLabel(user?.username || user?.email || '');
  }, [gigId, user]);

  const payload = useMemo(() => ({
    title,
    subtitle,
    summary,
    company,
    location,
    engagementType,
    compensation,
    estimatedHours,
    teamType,
    techStack,
    applyUrl,
    spec,
    specBlocks,
    epicTitle: epics[0]?.title || '',
    epicDescription: epics[0]?.description,
    architectureOverview,
    architectureBlocks,
    categoryId: categoryId || null,
    difficulty,
    isPublic,
    authorId: authorId || null,
    tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    epics: epics.map((epic, i) => ({
      id: epic.id,
      title: epic.title,
      description: epic.description,
      displayOrder: i + 1,
      clientKey: epic.clientKey,
    })),
    features: features.map((feature, i) => ({
      id: feature.id,
      title: feature.title,
      description: feature.description,
      displayOrder: i + 1,
      clientKey: feature.clientKey,
      epicClientKey: feature.epicClientKey,
    })),
    stories: stories.map((story, i) => ({
      id: story.id,
      featureClientKey: story.featureClientKey,
      clientKey: story.clientKey,
      title: story.title,
      description: story.description,
      acceptanceCriteria: story.acceptanceCriteria,
      displayOrder: i + 1,
    })),
    wireframes: wireframes.map((frame, i) => ({
      id: frame.id,
      title: frame.title,
      description: frame.description,
      mediaId: frame.mediaId,
      displayOrder: i + 1,
    })),
    errorMessages: errorMessages.map((item, i) => ({
      id: item.id,
      code: item.code,
      scenario: item.scenario,
      message: item.message,
      displayOrder: i + 1,
    })),
    sectionAssets: sectionAssets.map((item, i) => ({
      id: item.id,
      sectionKey: item.sectionKey,
      title: item.title,
      mediaId: item.mediaId,
      displayOrder: i + 1,
    })),
  }), [title, subtitle, summary, company, location, engagementType, compensation, estimatedHours, teamType, techStack, applyUrl, spec, specBlocks, architectureOverview, architectureBlocks, categoryId, difficulty, isPublic, authorId, tags, epics, features, stories, wireframes, errorMessages, sectionAssets]);

  const { isDirty, markClean } = useDirtyBaseline(payload, !loading);
  const { leaveOpen, stay, leaveWithoutSaving } = useUnsavedChangesGuard(isDirty);

  const save = async (submit = false) => {
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.saveProject(payload, gigId);
      const savedId = response.data.project.id;
      if (submit) {
        await contentApi.submitProject(savedId);
        setMessage(submitMessage(user));
      } else {
        setMessage('Draft saved.');
      }
      markClean();
      if (!gigId) navigate(`/admin/projects/${savedId}/edit`, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const runAi = async (generationType: string, prompt: string, action: 'generate' | 'revise', apply: (content: GeneratedContent, text: string) => void) => {
    setGenerating(true);
    setError(null);
    try {
      const response = await contentApi.generate({
        topic: title || 'Project',
        prompt: prompt || summary || `Help with the ${generationType} for this project.`,
        action,
        difficulty,
        generationType,
        existingContentJson: spec || undefined,
        targetProjectId: gigId,
      });
      const content = response.data.content as GeneratedContent;
      apply(content, generatedMarkdown(content));
      setMessage('AI draft ready. Review and edit before submitting.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'AI generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const uploadAsset = async (index: number, file: File) => {
    try {
      const response = await contentApi.uploadMedia(file, sectionAssets[index]?.title, undefined, {
        projectId: gigId,
        catalogKind: 'projects',
        entityName: title,
        topicName: sectionAssets[index]?.sectionKey || section,
      });
      setSectionAssets((current) => current.map((item, i) => i === index
        ? { ...item, title: item.title || file.name, mediaId: response.data.media.id, media: { id: response.data.media.id, url: response.data.media.url, contentType: response.data.media.contentType, fileName: response.data.media.fileName } }
        : item));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    }
  };

  const uploadWireframe = async (index: number, file: File) => {
    try {
      const response = await contentApi.uploadMedia(file, wireframes[index]?.title, undefined, {
        projectId: gigId,
        catalogKind: 'projects',
        entityName: title,
        topicName: 'wireframes',
      });
      setWireframes((current) => current.map((frame, i) => i === index
        ? { ...frame, mediaId: response.data.media.id, media: { id: response.data.media.id, url: response.data.media.url, contentType: response.data.media.contentType, fileName: response.data.media.fileName } }
        : frame));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Image upload failed.');
    }
  };

  if (!canWriteProjects(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have permission to create projects.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/dashboard?tab=projects" className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800">← Back to projects</Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{gigId ? 'Edit project' : 'New project'}</h1>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2">
            <Button type="button" variant="outline" isLoading={saving} onClick={() => save(false)}>Save draft</Button>
            <Button type="button" isLoading={saving} onClick={() => save(true)}>Submit for review</Button>
          </div>
          <ActionStatus className="w-full max-w-md" message={message} error={error} />
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[16rem_minmax(0,1fr)] gap-6 items-start">
        <aside className="lg:sticky lg:top-24 lg:self-start lg:z-20">
        <nav aria-label="Project sections" className="w-full rounded-2xl border border-gray-200 bg-white p-3 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
          <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Contents</p>
          {SECTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSection(item.id)}
              className={`w-full text-left rounded-lg px-3 py-2 text-sm ${
                section === item.id ? 'bg-primary-50 text-primary-800 font-semibold' : 'text-gray-800 hover:bg-gray-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
        </aside>

        <div className="min-w-0">
          {section === 'overview' && (
            <Card className="p-5 space-y-4">
              <ProjectAiPanel
                title="Generate overview with AI"
                placeholder="Describe the product, audience, and outcome."
                generating={generating}
                hasExisting={!!summary}
                onGenerate={(prompt, action) => void runAi('project-overview', prompt, action, (content, text) => {
                  if (content.title && !title) setTitle(content.title);
                  if (content.objective) setSummary(content.objective);
                  else if (text) setSummary(text);
                })}
              />
              <Input id="title" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <ContentOwnerSelect value={authorId} currentLabel={authorLabel} onChange={setAuthorId} />
              <Input id="subtitle" label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
              <label className="block text-sm font-medium text-gray-700">
                Summary
                <textarea className="mt-1 w-full rounded-md border-gray-300" rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} />
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <Input id="company" label="Company" value={company} onChange={(e) => setCompany(e.target.value)} />
                <Input id="location" label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
                <label className="block text-sm font-medium text-gray-700">
                  Engagement
                  <select className="mt-1 w-full rounded-md border-gray-300" value={engagementType} onChange={(e) => setEngagementType(e.target.value)}>
                    <option>Contract</option>
                    <option>Freelance</option>
                    <option>Internship</option>
                    <option>Full-time</option>
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Difficulty
                  <select className="mt-1 w-full rounded-md border-gray-300" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </label>
                <Input id="compensation" label="Compensation" value={compensation} onChange={(e) => setCompensation(e.target.value)} />
                <Input id="hours" label="Estimated hours" value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} />
                <label className="block text-sm font-medium text-gray-700">
                  Individual / group
                  <select className="mt-1 w-full rounded-md border-gray-300" value={teamType} onChange={(e) => setTeamType(e.target.value)}>
                    <option value="Individual">Individual</option>
                    <option value="Group">Group</option>
                  </select>
                </label>
                <Input id="techStack" label="Tech stack" value={techStack} onChange={(e) => setTechStack(e.target.value)} placeholder=".NET, React, SQL" />
              </div>
              <Input id="applyUrl" label="Apply URL (optional)" value={applyUrl} onChange={(e) => setApplyUrl(e.target.value)} placeholder="https://…" />
              <label className="block text-sm font-medium text-gray-700">
                Category
                <select className="mt-1 w-full rounded-md border-gray-300" value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">None</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <Input id="tags" label="Tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Comma separated" />
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                Public — visible to everyone after approval. Uncheck for a private project.
              </label>
            </Card>
          )}

          {section === 'spec' && (
            <Card className="p-5 space-y-4">
              <ProjectAiPanel
                title="Generate spec with AI"
                placeholder="Describe the problem, stack, and expected outcome."
                generating={generating}
                hasExisting={!!spec}
                onGenerate={(prompt, action) => void runAi('project-spec', prompt, action, (_c, text) => {
                  if (text) {
                    setSpec(text);
                    setSpecBlocks(blocksFromPlainText(text));
                  }
                })}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Specification</p>
                <BlockEditor
                  blocks={specBlocks}
                  uploadContext={{ projectId: gigId, catalogKind: 'projects', entityName: title, topicName: 'spec' }}
                  onChange={(blocks) => {
                    setSpecBlocks(blocks);
                    setSpec(blocks.map((b) => b.content || '').filter(Boolean).join('\n\n'));
                  }}
                />
              </div>
              <ProjectSectionAssets sectionKey="spec" assets={sectionAssets} onChange={setSectionAssets} onUpload={uploadAsset} />
            </Card>
          )}

          {section === 'requirements' && (
            <div className="space-y-6">
              <ProjectAiPanel
                title="Generate requirements with AI"
                placeholder="Describe epics, features, and stories you need."
                generating={generating}
                hasExisting={epics.some((e) => e.title)}
                onGenerate={(prompt, action) => void runAi('project-requirements', prompt, action, (content, text) => {
                  const headings = (content.sections || []).filter((s) => s.heading);
                  if (headings.length > 0) {
                    setEpics(headings.map((s, i) => ({
                      title: s.heading,
                      description: (s.blocks || []).map((b) => b.content).filter(Boolean).join('\n'),
                      displayOrder: i + 1,
                      clientKey: newKey(),
                    })));
                  } else if (text && epics[0]) {
                    setEpics((current) => current.map((epic, i) => i === 0 ? { ...epic, description: text } : epic));
                  }
                })}
              />
              <h2 className="font-semibold text-gray-900">Work items</h2>
              <ProjectRequirementsTree
                projectTitle={title}
                epics={epics}
                features={features}
                stories={stories}
                setEpics={setEpics}
                setFeatures={setFeatures}
                setStories={setStories}
              />

              <ProjectSectionAssets sectionKey="requirements" assets={sectionAssets} onChange={setSectionAssets} onUpload={uploadAsset} />
            </div>
          )}

          {section === 'wireframes' && (
            <div className="space-y-4">
              <ProjectAiPanel
                title="Generate wireframe notes with AI"
                placeholder="List the screens and flows to sketch."
                generating={generating}
                hasExisting={wireframes.length > 0}
                onGenerate={(prompt, action) => void runAi('project-wireframes', prompt, action, (content) => {
                  const screens = (content.sections || []).filter((s) => s.heading);
                  if (screens.length > 0) {
                    setWireframes(screens.map((s, i) => ({
                      title: s.heading,
                      description: (s.blocks || []).map((b) => b.content).filter(Boolean).join('\n'),
                      displayOrder: i + 1,
                    })));
                  }
                })}
              />
              <div className="flex justify-between items-center">
                <h2 className="font-semibold text-gray-900">UX wireframes</h2>
                <Button type="button" variant="outline" size="sm" onClick={() => setWireframes((current) => [...current, { title: '', description: '', displayOrder: current.length + 1 }])}>Add screen</Button>
              </div>
              {wireframes.map((frame, index) => (
                <Card key={index} className="p-4 space-y-3">
                  <Input id={`wire-title-${index}`} label="Screen title" value={frame.title} onChange={(e) => setWireframes((current) => current.map((item, i) => i === index ? { ...item, title: e.target.value } : item))} />
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                    <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} value={frame.description || ''} onChange={(e) => setWireframes((current) => current.map((item, i) => i === index ? { ...item, description: e.target.value } : item))} />
                  </label>
                  <input type="file" accept="image/*,.pdf,application/pdf" onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadWireframe(index, file); }} />
                  {frame.media?.url && (frame.media.contentType || '').startsWith('image/') && <img src={frame.media.url} alt="" className="h-40 rounded-lg object-cover" />}
                  {frame.media?.url && !(frame.media.contentType || 'image/').startsWith('image/') && (
                    <a href={frame.media.url} className="text-sm text-primary-700" target="_blank" rel="noreferrer">{frame.media.fileName || 'Open file'}</a>
                  )}
                  <button type="button" className="text-sm text-error-600" onClick={() => setWireframes((current) => current.filter((_, i) => i !== index))}>Remove</button>
                </Card>
              ))}
            </div>
          )}

          {section === 'architecture' && (
            <Card className="p-5 space-y-4">
              <ProjectAiPanel
                title="Generate architecture with AI"
                placeholder="Describe services, data stores, and integrations."
                generating={generating}
                hasExisting={!!architectureOverview}
                onGenerate={(prompt, action) => void runAi('project-architecture', prompt, action, (_c, text) => {
                  if (text) {
                    setArchitectureOverview(text);
                    setArchitectureBlocks(blocksFromPlainText(text));
                  }
                })}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Architecture overview</p>
                <BlockEditor
                  blocks={architectureBlocks}
                  uploadContext={{ projectId: gigId, catalogKind: 'projects', entityName: title, topicName: 'architecture' }}
                  onChange={(blocks) => {
                    setArchitectureBlocks(blocks);
                    setArchitectureOverview(blocks.map((b) => b.content || '').filter(Boolean).join('\n\n'));
                  }}
                />
              </div>
              <ProjectSectionAssets sectionKey="architecture" assets={sectionAssets} onChange={setSectionAssets} onUpload={uploadAsset} />
            </Card>
          )}

          {section === 'errors' && (
            <div className="space-y-4">
              <ProjectAiPanel
                title="Generate error catalog with AI"
                placeholder="Describe failure cases and user-facing messages."
                generating={generating}
                hasExisting={errorMessages.length > 0}
                onGenerate={(prompt, action) => void runAi('project-errors', prompt, action, (content) => {
                  const rows = (content.sections || []).filter((s) => s.heading);
                  if (rows.length > 0) {
                    setErrorMessages(rows.map((s, i) => ({
                      code: '',
                      scenario: s.heading,
                      message: (s.blocks || []).map((b) => b.content).filter(Boolean).join('\n'),
                      displayOrder: i + 1,
                    })));
                  }
                })}
              />
              <div className="flex justify-between items-center">
                <h2 className="font-semibold text-gray-900">Error messages</h2>
                <Button type="button" variant="outline" size="sm" onClick={() => setErrorMessages((current) => [...current, { code: '', scenario: '', message: '', displayOrder: current.length + 1 }])}>Add error</Button>
              </div>
              {errorMessages.map((item, index) => (
                <Card key={index} className="p-4 space-y-3">
                  <Input id={`err-code-${index}`} label="Code" value={item.code || ''} onChange={(e) => setErrorMessages((current) => current.map((row, i) => i === index ? { ...row, code: e.target.value } : row))} />
                  <Input id={`err-scenario-${index}`} label="Scenario" value={item.scenario} onChange={(e) => setErrorMessages((current) => current.map((row, i) => i === index ? { ...row, scenario: e.target.value } : row))} />
                  <label className="block text-sm font-medium text-gray-700">
                    Message
                    <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} value={item.message} onChange={(e) => setErrorMessages((current) => current.map((row, i) => i === index ? { ...row, message: e.target.value } : row))} />
                  </label>
                  <button type="button" className="text-sm text-error-600" onClick={() => setErrorMessages((current) => current.filter((_, i) => i !== index))}>Remove</button>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-6">
        <Button type="button" variant="outline" isLoading={saving} onClick={() => save(false)}>Save draft</Button>
        <Button type="button" isLoading={saving} onClick={() => save(true)}>Submit for review</Button>
      </div>
      <UnsavedLeaveDialog open={leaveOpen} onStay={stay} onLeave={leaveWithoutSaving} />
    </div>
  );
};

export default ProjectEditorPage;
